<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
        <PageHeader
            title="Order & Sales Management"
            :showBtn="false"
            :showAddNew="true"
            btnTitle="New Order"
            :showExtraBtn="true"
            extraBtnTitle="Register"
            @onClickAddNewButton="navigateToAddPage"
            @onClickExtraBtn="navigateToSaleRegisterPage"
        >
            <template #extra-btn>
                <button type="button" class="btn page-head-btn page-head-btn-ghost" @click="onClickToExport">
                    <i class="fa-regular fa-file-excel me-1"></i> Export
                </button>
            </template>
        </PageHeader>

        <div class="px-2">
            <DateQuerySetter
                class="filter-row"
                btnTitle="Go"
                @onClickGo="onClickGo"
            >
                <div class="col-auto d-flex align-items-end mt-2">
                    <FilterPopover
                        ref="filterPopoverRef"
                        title="Filters"
                        popover-width="640px"
                        :active-count="activeFilterCount"
                        @open="onOpenFilter"
                        @apply="onApplyFilters"
                        @clear="onClearFilters"
                    >
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">From Date (Invoice)</label>
                            <input
                                v-model="draftFilters.invoice_start_date"
                                type="date"
                                class="form-control date-mask"
                            >
                        </div>
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">To Date (Invoice)</label>
                            <input
                                v-model="draftFilters.invoice_end_date"
                                type="date"
                                class="form-control date-mask"
                            >
                        </div>
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">Sales Person</label>
                            <AsyncSelect
                                placeholder="Select Sales Person"
                                v-model="draftFilters.sales_person"
                                :api-service="fetchAllSR"
                                :reduce="option => option"
                                :option-selected="onChangeSalesPerson"
                            />
                        </div>
                        <div class="col-12 col-sm-6">
                            <div class="d-flex align-items-center justify-content-between filter-label">
                                <label class="form-label mb-0">Customer</label>
                                <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
                                    <i class="fas fa-search"></i>
                                </span>
                            </div>
                            <AsyncSelect
                                placeholder="Select Customer"
                                v-model="draftFilters.contact_profile"
                                :api-service="fetchContactProfiles"
                                :additional-query="additionalContactOption"
                                :reduce="option => option"
                                :key="contactRenderKey"
                            />
                        </div>
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">Order No</label>
                            <input
                                v-model="draftFilters.order_no"
                                type="text"
                                class="form-control"
                                placeholder="Order No"
                            >
                        </div>
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">Invoice No</label>
                            <input
                                v-model="draftFilters.invoice_no"
                                type="text"
                                class="form-control"
                                placeholder="Invoice No"
                            >
                        </div>
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">Status</label>
                            <v-select
                                placeholder="Select Status"
                                v-model="draftFilters.status"
                                :options="orderStatuses"
                                label="name"
                                :reduce="option => option.value"
                            />
                        </div>
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">Invoice Status</label>
                            <v-select
                                placeholder="Select Invoice Status"
                                v-model="draftFilters.invoice_status"
                                :options="invoiceStatuses"
                                label="name"
                                :reduce="option => option.value"
                            />
                        </div>
                        <div class="col-12 col-sm-6">
                            <label class="form-label filter-label">Order Type</label>
                            <v-select
                                placeholder="Select Order Type"
                                v-model="draftFilters.sale_type"
                                :options="orderTypes"
                                label="name"
                                :reduce="option => option.value"
                            />
                        </div>
                    </FilterPopover>
                </div>
            </DateQuerySetter>
        </div>
        <div class="col-12 px-2 mt-2">
            <ListTable
                :purchase="purchaseList"
                route_name="product-sales-order-edit"
                :fromApproval="fromApproval"
                @orderStatusChanged="onClickGo"
            />
        </div>
        <div class="px-2">
            <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

        <ContactDropdownFilter
            v-if="store.state.isModalOpenThree"
            @onSearchContact="onSearchContact"
            :companyRoles="companyRoles"
            :isFilterable="true"
        />
    </div>
</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import PageHeader from '@/components/atom/PageHeader.vue'
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import Pagination from '@/components/atom/Pagination'
import ListTable from '@/components/molecule/company/inventory/order-sales/ListTable.vue'
import handleContact from "@/services/modules/contact";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleCrm from '@/services/modules/crm/crm'
import handleRole from "@/services/modules/role";
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import Loader from '@/components/atom/LoaderComponent'

const router = useRouter()
const store = useStore()
const route = useRoute()
const offset = ref(20)
const purchaseList = ref([])
const loading = ref(false)
const contactRenderKey = ref(0)
const filterPopoverRef = ref(null)

const { fetchSalesOrderList, fetchOrderStatus, fetchSalesOrderTypes } = handlePurchase()
const { fetchContactProfiles } = handleContact()
const { fetchCompanyDefaultRoles } = handleRole()
const { fetchAllSR } = handleCrm()
const additionalContactOption = ref({
    with_mobile: 1,
    dealer_id: '',
    roles: 'customer,lead'
})
const orderTypes = ref([])
const companyRoles = ref(null)
const invoiceStatuses = ref([])
const orderStatuses = ref([])

const getEmptyFilters = () => ({
    sales_person: null,
    contact_profile: null,
    invoice_start_date: null,
    invoice_end_date: null,
    order_no: '',
    invoice_no: '',
    status: null,
    invoice_status: null,
    sale_type: null
})
const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const isPickingContact = ref(false)

const activeFilterCount = computed(() => {
    return Object.entries(appliedFilters.value)
        .filter(([key, value]) => key !== 'sales_person' && !! value)
        .length
})

const companyId = computed(() => {
    return route.params.companyId
})
const startDate = computed(() => {
    return route.query.start
})
const endDate = computed(() => {
    return route.query.end
})
const fromApproval = computed(() => {
    return route.name === 'sales-order-approval'
})

const getCompanyRoles = async () => {
    const res = await fetchCompanyDefaultRoles(`?company_id=${companyId.value}&roles=customer,lead`)
    if (! res.status) return
    companyRoles.value = res.data
}

const getSaleOrderTypes = async () => {
    const res = await fetchSalesOrderTypes(`?company_id=${route.params.companyId}`)
    if (! res.status) return
    orderTypes.value = res.data
}

const getOrderStatus = async () => {
    const res = await fetchOrderStatus('?company_id=' + companyId.value)
    if (! res.status) return
    invoiceStatuses.value = res.data.filter(item => ['created', 'pending'].includes(item.value))
    orderStatuses.value = res.data.filter(item => ['pending', 'approved', 'cancelled'].includes(item.value))
}

const onOpenContactSearchModal = () => {
    isPickingContact.value = true
    store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
    draftFilters.value.contact_profile = contact
    if (! filterPopoverRef.value) return
    filterPopoverRef.value.open()
}

const onChangeSalesPerson = () => {
    draftFilters.value.contact_profile = null
    contactRenderKey.value += 1
}

const onOpenFilter = () => {
    if (isPickingContact.value) {
        isPickingContact.value = false
        return
    }
    draftFilters.value = Object.assign({}, appliedFilters.value)
}

const onApplyFilters = () => {
    appliedFilters.value = Object.assign({}, draftFilters.value)
    reloadFromFirstPage()
}

const onClearFilters = () => {
    appliedFilters.value = getEmptyFilters()
    draftFilters.value = getEmptyFilters()
    reloadFromFirstPage()
}

const reloadFromFirstPage = async () => {
    store.commit('resetPagination')
    if (route.query.page && route.query.page !== '1') {
        await router.push({ path: route.path, query: { ...route.query, page: 1 } })
    }
    onClickGo()
}

function navigateToAddPage() {
    const query = Object.assign({}, route.query)
    query.type = 'order-sales'
    router.push({ name: 'product-sales-order', params: route.params, query: query })
}

function navigateToSaleRegisterPage() {
    const query = Object.assign({}, route.query)
    query.type = 'order-sales'
    router.push({ name: 'product-sales-register', params: route.params, query: query })
}

function onClickToExport() {
    const token = localStorage.getItem('token')
    const query = getQuery().replace(/&page=\d+/g, '')
    const url = `${process.env.VUE_APP_BASE_URL}/api/inventory/sales/order${query}&_token=${token}&is_export=1&is_order_sales=1`
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.click()
}

function getQuery() {
    const filters = appliedFilters.value
    let query = '?company_id=' + companyId.value
    if (! startDate.value && ! endDate.value) return query
    if (filters.contact_profile) query += '&contact_profile_id=' + filters.contact_profile.id
    if (startDate.value) query += '&start_date=' + startDate.value
    if (endDate.value) query += '&end_date=' + endDate.value
    if (route.query.page) query += '&page=' + route.query.page
    if (filters.status) query += '&status=' + filters.status
    if (filters.invoice_status) query += '&invoice_status=' + filters.invoice_status
    if (filters.invoice_start_date) query += '&invoice_start_date=' + filters.invoice_start_date
    if (filters.invoice_end_date) query += '&invoice_end_date=' + filters.invoice_end_date
    if (filters.order_no) query += '&order_no=' + filters.order_no
    if (filters.invoice_no) query += '&invoice_no=' + filters.invoice_no
    if (filters.sale_type) query += '&sale_type=' + filters.sale_type
    query += '&offset=' + offset.value
    return query
}

const onClickGo = () => {
    loading.value = true
    return fetchSalesOrderList(getQuery())
        .then(res => {
            if (! res.status) {
                purchaseList.value = []
                store.commit('resetPagination')
                return
            }
            purchaseList.value = res.date
            setPagination(res.date)
        })
        .catch(() => {})
        .finally(() => {
            loading.value = false
        })
}

function setPagination(data) {
    if (! data.data.length) return
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({ path: route.path, query: routeQuery })
    await onClickGo()
}

onMounted(() => {
    onClickGo()
    getOrderStatus()
    getCompanyRoles()
    getSaleOrderTypes()
})
</script>

<style scoped>
.filter-label {
    padding-right: 0.5rem !important;
}
.page-head-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    padding: 0 16px;
    white-space: nowrap;
}
.page-head-btn-ghost {
    border: 1px solid #e4e7ec;
    background: #fff;
    color: #475467;
}
.page-head-btn-ghost:hover {
    background: #f9fafb;
    color: #101828;
}
.filter-row {
    position: relative;
}
.filter-row :deep(.filter-pop-wrap) {
    position: static;
}
.filter-row :deep(.filter-pop-wrap .filter-pop) {
    left: calc(var(--bs-gutter-x) * .5);
    right: auto;
    width: calc(100% - var(--bs-gutter-x)) !important;
    max-width: 640px;
}
</style>
